import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { Button, Spinner, Icon, Alert, Intent } from "@blueprintjs/core";
import QuickbooksCustomer from './QuickbooksCustomer';
import { observable } from "mobx";
import Toast from "helpers/toaster";
import { DebounceInput } from 'react-debounce-input';
import FormDrawer from "components/FormDrawer";
import { FormSection } from "components/FormBits";

// Link an invoice's client to a QuickBooks customer, in a side panel: find an
// existing QuickBooks customer and link it, or create a new one.
// Styles: .qb-* in styles/form-drawer.scss.
@inject('invoiceStore', 'quickbooksStore') @observer
class SyncWithQuickbooksModal extends Component {

  @observable isCreateNewCustomer = false;

  handleCloseDailog() {
    this.props.onClose();
  }

  createQuickbooksCustomer() {
    this.isCreateNewCustomer = true
  }

  onSuccessfulLinkCustomer() {
    this.props.onSuccess(null)
  }

  handleCreateNeCustomerCancel() {
    this.isCreateNewCustomer = false
  }

  handleSaveConfirm() {
    this.isCreateNewCustomer = false
    this.props.quickbooksStore.createQuickbooksCustomer()
      .then((response) => {
        Toast.show({ message: "Customer created in QuickBooks.", intent: Intent.SUCCESS });
        this.props.onSuccess(response.data);
      })
      .catch((error) => {
        const auth = error.response && error.response.data && error.response.data.auth;
        Toast.show({
          message: auth || "Couldn’t create the customer in QuickBooks. Reconnect QuickBooks in Settings and try again.",
          intent: Intent.DANGER
        });
      })
  }

  onChangeSearchText(event) {
    this.props.quickbooksStore.searchQuickbooksCustomers(event.target.value)
  }

  renderClient(client, address) {
    return (
      <div className="qb-client">
        <span className="qb-client__avatar" aria-hidden="true">{initials(client.name)}</span>
        <div className="qb-client__text">
          <div className="qb-client__name">{client.name}</div>
          <div className="qb-client__meta">
            {client.phone && <span><Icon icon="phone" iconSize={11} />{client.phone}</span>}
            {client.email && <span><Icon icon="envelope" iconSize={11} />{client.email}</span>}
            {address && <span><Icon icon="map-marker" iconSize={11} />{address}</span>}
          </div>
        </div>
        <span className="qb-client__status">Not linked yet</span>
      </div>
    );
  }

  render() {
    const { object } = this.props.invoiceStore.invoice;
    const {
      isLoadingCreateQuickbooksCustomer,
      quickbooksSearchCustomers,
      quickbooksCustomers: { isLoading, objects }
    } = this.props.quickbooksStore;
    const client = object.attributes && object.attributes.client;
    const address = object.attributes && object.attributes.location && object.attributes.location.full_address;
    const searching = isLoading || quickbooksSearchCustomers;
    const linked = client && client.synced_with_quickbooks;

    return (
      <React.Fragment>
        <FormDrawer
          isOpen={this.props.isOpen}
          title="Link to QuickBooks"
          icon="link"
          tone="billing"
          size="min(760px, 100vw)"
          onClose={this.handleCloseDailog.bind(this)}
          footer={<Button text="Close" onClick={this.handleCloseDailog.bind(this)} />}
        >
          {client && linked && (
            <FormSection title="Client" hint="Who this invoice is for.">
              <p className="qb-done"><Icon icon="tick-circle" iconSize={14} /> {client.name} is linked to a QuickBooks customer.</p>
            </FormSection>
          )}

          {client && !linked && (
            <React.Fragment>
              <FormSection title="Client" hint="Who this invoice is for in Field Nexus.">
                {this.renderClient(client, address)}
              </FormSection>

              <FormSection title="Find in QuickBooks" hint="Link to a customer that’s already in QuickBooks.">
                <div className="qb-search">
                  <Icon icon="search" iconSize={14} />
                  <DebounceInput
                    minLength={2}
                    debounceTimeout={700}
                    className="bp3-input"
                    type="search"
                    placeholder="Search QuickBooks customers by company name"
                    aria-label="Search QuickBooks customers"
                    onChange={(event) => this.onChangeSearchText(event)}
                  />
                </div>
                <div className="qb-results" aria-busy={searching}>
                  {searching && <div className="qb-results__status"><Spinner size={16} /> Searching QuickBooks…</div>}
                  {!searching && objects && objects.length > 0 && (
                    <ul className="qb-results__list">
                      {objects.map((customer) => (
                        <QuickbooksCustomer
                          customer={customer}
                          clientName={client.name}
                          onSuccess={this.onSuccessfulLinkCustomer.bind(this)}
                          key={customer.id || customer.display_name}
                        />
                      ))}
                    </ul>
                  )}
                  {!searching && objects && objects.length === 0 && (
                    <div className="qb-results__status">
                      No QuickBooks customers match. Try another name, or create a new customer below.
                    </div>
                  )}
                </div>
              </FormSection>

              <FormSection title="Or create one" hint="Add this client to QuickBooks as a new customer.">
                <button
                  type="button"
                  className="qb-create"
                  onClick={this.createQuickbooksCustomer.bind(this)}
                  disabled={isLoadingCreateQuickbooksCustomer}
                >
                  {isLoadingCreateQuickbooksCustomer
                    ? <Spinner size={16} />
                    : <img src="/quickbooks_logo_small.png" alt="" />}
                  Create customer in QuickBooks
                </button>
              </FormSection>
            </React.Fragment>
          )}
        </FormDrawer>

        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Create customer"
          icon="add"
          intent={Intent.PRIMARY}
          isOpen={this.isCreateNewCustomer}
          onCancel={this.handleCreateNeCustomerCancel.bind(this)}
          onConfirm={this.handleSaveConfirm.bind(this)}
        >
          <strong>Create a QuickBooks customer for {client && client.name}?</strong>
          <p>They’re added to QuickBooks with these details and linked to this client.</p>
          {client && client.email && <p>{client.email}{client.phone ? `, ${client.phone}` : ''}</p>}
        </Alert>
      </React.Fragment>
    )
  }

}

const initials = (name = '') =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

export default SyncWithQuickbooksModal;
